import { type JanusCtx, type Row, ecLean, requiredInt } from './context.js';
import { discernVariableTags } from './discern-variable-tags.js';
import { adjustCharges } from './charges/adjust-charges.js';
import { abilityEntryId, rawAbilityChargeEntry } from './charges/charge-entry.js';
import { setMana } from './resource-reduction.js';
import { findChargeEntry } from '../charges/charge-adjust.js';
import { encounterInCombat, ETHER_MECHANIC_ID, PSI_MECHANIC_ID } from '../economy/out-of-combat.js';

/** Target resource effects use the same writers as self costs, including their reduction shields. */
export function applyTargetResources(ctx: JanusCtx, ability: Row, target: Row, variable: unknown): void {
    if (ctx.read.has(ability, 'change_mana_for_target')) {
        const delta = discernVariableTags(ctx, ctx.read.value(ability, 'change_mana_for_target'), target,
            { reason: 'change_mana_for_target', integer: true, x: variable });
        const before = requiredInt(ecLean(target, 'mana'), 'change_mana_for_target:mana-not-held');
        const spirit = requiredInt(ctx.read.value(target, 'spirit'), 'change_mana_for_target:spirit-not-held');
        setMana(ctx, target, Math.max(0, Math.min(before + delta, spirit)));
    }
    for (const [layer, qtyLayer, sign] of [
        ['consume_ability_charges_for_target', 'consume_ability_charges_qty_for_target', -1],
        ['add_charges_to_ability_for_target', 'add_charges_to_ability_qty_for_target', 1],
    ] as const) {
        for (const entry of ctx.read.entries(ability, layer)) {
            const id = abilityEntryId(entry);
            if (!findChargeEntry(target, id, ctx.read)) continue;
            const pool = rawAbilityChargeEntry(ctx, target, id);
            const raw = ctx.read.value(ability, qtyLayer) ?? 1;
            const qty = raw === '[max]' ? pool.max : raw === '[any]' ? pool.current : raw === '[x]'
                ? Number(variable ?? ctx.read.value(ability, 'menta_level') ?? ctx.read.value(ability, 'spell_level') ?? 1)
                : Number(raw);
            const amount = raw === '[max]' || raw === '[any]' || raw === '[x]' ? qty : Math.max(1, qty);
            if (sign * amount < 0 && (id === ETHER_MECHANIC_ID || id === PSI_MECHANIC_ID) && encounterInCombat(ctx.encounter) === false) continue;
            adjustCharges(ctx, target, id, sign * requiredInt(amount, `${layer}:invalid-quantity`), true);
        }
    }
}
